import React, { useState } from "react";
import { TextField } from "@/components/form/fields";
import { formatPhoneNumber, validatePhone } from "../validation";

interface PhoneFieldProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onValidationChange?: (isValid: boolean, message: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  validateOnBlur?: boolean;
  /** Accessible name when the visible label is hidden (e.g. a table row). */
  ariaLabel?: string;
}

const PhoneField: React.FC<PhoneFieldProps> = ({
  id = "phoneNumber",
  name = "phoneNumber",
  value,
  onChange,
  onBlur,
  onValidationChange,
  label = "Phone Number",
  placeholder = "(123) 456-7890",
  required = false,
  error = false,
  hint,
  className = "",
  validateOnBlur = true,
  ariaLabel,
}) => {
  const [localError, setLocalError] = useState<string>("");

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits for phone input
    const rawValue = e.target.value;
    const digits = rawValue.replace(/\D/g, "");
    
    // If non-digit characters were entered and removed, create a new input change event
    if (digits !== rawValue.replace(/[\(\)\s-]/g, "")) {
      const sanitizedEvent = {
        ...e,
        target: {
          ...e.target,
          value: digits
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      // Format the digits properly
      const formatted = formatPhoneNumber(digits);
      
      // Create a new event with the formatted value
      const formattedEvent = {
        ...e,
        target: {
          ...e.target,
          name: name,
          value: formatted
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(formattedEvent);
    } else {
      // Format the phone number using the utility function
      const formatted = formatPhoneNumber(e.target.value);
      
      // Create a new event with the formatted value
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          name: name,
          value: formatted
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(newEvent);
    }
    
    // Clear any local error when user is typing
    if (localError) {
      setLocalError("");
    }
  };

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (validateOnBlur) {
      // Count actual digits
      const digits = e.target.value.replace(/\D/g, "");
      
      // Custom validation for digit count
      let isValid = true;
      let errorMessage = "";
      
      if (digits.length > 0 && digits.length < 10) {
        isValid = false;
        errorMessage = `Phone number must have 10 digits. You entered ${digits.length} digits.`;
      } else {
        // Use standard phone validation
        const validationResult = validatePhone(e.target.value, required);
        isValid = validationResult.valid;
        errorMessage = validationResult.message;
      }
      
      // Set local error state
      setLocalError(isValid ? "" : errorMessage);
      
      // Report validation status to parent component if callback exists
      if (onValidationChange) {
        onValidationChange(isValid, errorMessage);
      }
      
      // NOTE: Removed problematic validation events through onChange
      // These were causing form fields to display 'true'/'false' instead of phone numbers
      // Validation is already handled through the onValidationChange callback above
    }
    
    // Call the provided onBlur if exists
    if (onBlur) {
      onBlur(e);
    }
  };

  // Determine which error message to show
  const displayError = localError || (typeof error === 'string' ? error : hint);

  return (
    <div className={className}>
      <TextField
        id={id}
        name={name}
        label={label}
        type="tel"
        placeholder={placeholder}
        value={value}
        onChange={handlePhoneChange}
        onBlur={handlePhoneBlur}
        error={!!error || !!localError}
        hint={displayError}
        required={required}
        inputMode="tel"
        maxLength={14}
        ariaLabel={ariaLabel}
      />
    </div>
  );
};

export default PhoneField; 